import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Search, Filter, Star, StarOff, Mail, MailOpen, Trash2,
  MoreHorizontal, User, Phone, Clock,
  Inbox, ArrowLeft, FileText, Download, Loader2, X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { submissionsApi } from '@/api/landingPages';

const ALL = 'all';
const EMPTY_STATS = { total: 0, unread: 0, starred: 0, today: 0 };
const EMPTY_OPTIONS = { pages: [], campaigns: [], utm_campaigns: [] };

const UTM_LABELS = {
  utm_campaign: 'Campaign',
  utm_source: 'Source',
  utm_medium: 'Medium',
  utm_content: 'Content',
  utm_term: 'Term',
};

export default function Submissions() {
  // The URL holds the filters (?page_id=, ?campaign_id=, ?utm_campaign=,
  // ?from=, ?to=) so a filtered inbox can be bookmarked or linked to from a
  // landing page.
  const [searchParams, setSearchParams] = useSearchParams();
  const pageIdFilter = searchParams.get('page_id') || null;
  const campaignFilter = searchParams.get('campaign_id') || null;
  const utmCampaignFilter = searchParams.get('utm_campaign') || null;
  const dateFrom = searchParams.get('from') || '';
  const dateTo = searchParams.get('to') || '';

  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [readFilter, setReadFilter] = useState('all');
  const [starredFilter, setStarredFilter] = useState('all');
  const [pagination, setPagination] = useState({ total: 0, page: 1, per_page: 50, has_more: false });
  const [stats, setStats] = useState(EMPTY_STATS);
  const [options, setOptions] = useState(EMPTY_OPTIONS);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const pageTitles = useMemo(
    () => Object.fromEntries(options.pages.map((p) => [p.id, p.title])),
    [options.pages]
  );
  const scopeFiltered = !!(pageIdFilter || campaignFilter || utmCampaignFilter || dateFrom || dateTo);

  const setFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value && value !== ALL) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
  };

  const clearFilters = () => setSearchParams(new URLSearchParams(), { replace: true });

  // Filters shared by the list, the stat cards and the export.
  const scopeParams = () => {
    const params = {};
    if (pageIdFilter) params.landing_page_id = pageIdFilter;
    if (campaignFilter) params.campaign_id = campaignFilter;
    if (utmCampaignFilter) params.utm_campaign = utmCampaignFilter;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (searchQuery) params.search = searchQuery;
    return params;
  };

  const listParams = () => {
    const params = scopeParams();
    if (readFilter === 'unread') params.read = false;
    if (readFilter === 'read') params.read = true;
    if (starredFilter === 'starred') params.starred = true;
    return params;
  };

  // ?submission=<id> (e.g. from a lead's activity log) opens that submission.
  const submissionParam = searchParams.get('submission');
  useEffect(() => {
    if (!submissionParam) return;
    submissionsApi.get(submissionParam)
      .then((data) => {
        if (data?.success && data.data) {
          setSelectedSubmission(data.data);
          setDetailOpen(true);
        } else {
          toast.error('Submission not found');
        }
      })
      .catch(() => toast.error('Submission not found'));
  }, [submissionParam]);

  useEffect(() => {
    submissionsApi.filterOptions()
      .then((data) => { if (data?.success) setOptions(data.data || EMPTY_OPTIONS); })
      .catch((error) => console.error('Error fetching filter options:', error));
  }, []);

  useEffect(() => {
    fetchSubmissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readFilter, starredFilter, pageIdFilter, campaignFilter, utmCampaignFilter, dateFrom, dateTo]);

  const fetchStats = async () => {
    try {
      const data = await submissionsApi.stats(scopeParams());
      if (data?.success) setStats(data.data || EMPTY_STATS);
    } catch (error) {
      console.error('Error fetching submission stats:', error);
    }
  };

  const fetchSubmissions = async () => {
    setLoading(true);
    fetchStats();
    try {
      const data = await submissionsApi.listAll(listParams());
      if (data.success) {
        setSubmissions(data.data || []);
        setPagination(data.pagination || { total: 0, page: 1, per_page: 50, has_more: false });
      }
    } catch (error) {
      console.error('Error fetching submissions:', error);
      toast.error('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const result = await submissionsApi.exportCsv(listParams());
      if (result.truncated) {
        toast.warning(`Exported the newest ${result.rows.toLocaleString()} of ${result.total.toLocaleString()} submissions. Narrow the filters to export the rest.`);
      } else {
        toast.success(`Exported ${result.rows.toLocaleString()} submission${result.rows === 1 ? '' : 's'}`);
      }
    } catch (error) {
      console.error('Error exporting submissions:', error);
      toast.error('Failed to export submissions');
    } finally {
      setExporting(false);
    }
  };

  const handleViewSubmission = async (submission) => {
    setSelectedSubmission(submission);
    setDetailOpen(true);

    // Mark as read
    if (!submission.read) {
      try {
        await submissionsApi.markRead(submission.id, true);
        setSubmissions(submissions.map(s =>
          s.id === submission.id ? { ...s, read: true } : s
        ));
        setStats(prev => ({ ...prev, unread: Math.max(0, prev.unread - 1) }));
      } catch (error) {
        console.error('Error marking read:', error);
      }
    }
  };

  const handleToggleStar = async (id, currentStarred) => {
    try {
      await submissionsApi.star(id, !currentStarred);
      setSubmissions(submissions.map(s =>
        s.id === id ? { ...s, starred: !currentStarred } : s
      ));
      setStats(prev => ({ ...prev, starred: Math.max(0, prev.starred + (currentStarred ? -1 : 1)) }));
      toast.success(currentStarred ? 'Unstarred' : 'Starred');
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleToggleRead = async (id, currentRead) => {
    try {
      await submissionsApi.markRead(id, !currentRead);
      setSubmissions(submissions.map(s =>
        s.id === id ? { ...s, read: !currentRead } : s
      ));
      setStats(prev => ({ ...prev, unread: Math.max(0, prev.unread + (currentRead ? 1 : -1)) }));
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleArchive = async (id) => {
    if (!window.confirm('Archive this submission?')) return;
    try {
      await submissionsApi.archive(id);
      setSubmissions(submissions.filter(s => s.id !== id));
      fetchStats();
      toast.success('Archived');
      setDetailOpen(false);
    } catch (error) {
      toast.error('Failed to archive');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;

    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;

    return date.toLocaleDateString('en-ZA', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  const consentLabel = (value) => (value === true ? 'Opted in' : value === false ? 'Not opted in' : null);

  return (
    <div className="space-y-6" data-testid="submissions-page">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">
            {pageIdFilter ? 'Submissions for this Page' : 'Form Submissions'}
          </h1>
          <p className="text-muted-foreground">
            {pageIdFilter
              ? 'Filtered to one landing page'
              : 'View, filter and export leads from your landing pages'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {stats.unread > 0 && (
            <Badge variant="default" className="text-sm">
              {stats.unread} unread
            </Badge>
          )}
          <Button
            onClick={handleExport}
            disabled={exporting || pagination.total === 0}
            data-testid="export-csv"
          >
            {exporting
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <Download className="w-4 h-4 mr-2" />}
            Export CSV
          </Button>
        </div>
      </div>

      {pageIdFilter && (
        <Card className="border-primary/30 bg-primary/5">
          <CardContent className="py-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <FileText className="h-5 w-5 text-primary" />
              <p className="text-sm font-medium">
                Filtered to landing page:{' '}
                {pageTitles[pageIdFilter]
                  ? <span>{pageTitles[pageIdFilter]}</span>
                  : <code className="bg-muted px-1 rounded">{pageIdFilter}</code>}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" asChild>
                <Link to={`/pages/${pageIdFilter}`}>Open page</Link>
              </Button>
              <Button variant="outline" size="sm" asChild>
                <Link to="/submissions">
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  All submissions
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats: server-side counts across every page of results */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Submissions</CardDescription>
            <CardTitle className="text-2xl" data-testid="stat-total">{stats.total}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Unread</CardDescription>
            <CardTitle className="text-2xl">{stats.unread}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Starred</CardDescription>
            <CardTitle className="text-2xl">{stats.starred}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Today</CardDescription>
            <CardTitle className="text-2xl">{stats.today}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Landing page</Label>
              <Select value={pageIdFilter || ALL} onValueChange={(v) => setFilter('page_id', v)}>
                <SelectTrigger data-testid="filter-page"><SelectValue placeholder="All landing pages" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>All landing pages</SelectItem>
                  {pageIdFilter && !pageTitles[pageIdFilter] && (
                    <SelectItem value={pageIdFilter}>{pageIdFilter}</SelectItem>
                  )}
                  {options.pages.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.title} ({p.submissions})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Campaign</Label>
              <Select value={campaignFilter || ALL} onValueChange={(v) => setFilter('campaign_id', v)}>
                <SelectTrigger data-testid="filter-campaign"><SelectValue placeholder="All campaigns" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>All campaigns</SelectItem>
                  {options.campaigns.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">UTM campaign tag</Label>
              <Select value={utmCampaignFilter || ALL} onValueChange={(v) => setFilter('utm_campaign', v)}>
                <SelectTrigger data-testid="filter-utm"><SelectValue placeholder="Any tag" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>Any tag</SelectItem>
                  {options.utm_campaigns.map((u) => (
                    <SelectItem key={u} value={u}>{u}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground" htmlFor="filter-from">From</Label>
              <Input
                id="filter-from"
                type="date"
                value={dateFrom}
                max={dateTo || undefined}
                onChange={(e) => setFilter('from', e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground" htmlFor="filter-to">To</Label>
              <Input
                id="filter-to"
                type="date"
                value={dateTo}
                min={dateFrom || undefined}
                onChange={(e) => setFilter('to', e.target.value)}
              />
            </div>
          </div>
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search submissions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && fetchSubmissions()}
                className="pl-9"
                data-testid="search-input"
              />
            </div>
            <Select value={readFilter} onValueChange={setReadFilter}>
              <SelectTrigger className="md:w-[140px]">
                <SelectValue placeholder="Read Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="unread">Unread</SelectItem>
                <SelectItem value="read">Read</SelectItem>
              </SelectContent>
            </Select>
            <Select value={starredFilter} onValueChange={setStarredFilter}>
              <SelectTrigger className="md:w-[140px]">
                <SelectValue placeholder="Starred" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="starred">Starred Only</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchSubmissions}>
              <Filter className="w-4 h-4 mr-2" />
              Apply
            </Button>
            {scopeFiltered && (
              <Button variant="ghost" onClick={clearFilters} data-testid="clear-filters">
                <X className="w-4 h-4 mr-2" />
                Clear filters
              </Button>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            Export CSV downloads every submission matching these filters, not just this page of results.
          </p>
        </CardContent>
      </Card>

      {/* Submissions Table */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40px]"></TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Landing page</TableHead>
                <TableHead>Message</TableHead>
                <TableHead>Campaign tag</TableHead>
                <TableHead>Submitted</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton className="h-4 w-4" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[180px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[140px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[200px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[100px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[60px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[30px]" /></TableCell>
                  </TableRow>
                ))
              ) : submissions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12">
                    <div className="flex flex-col items-center gap-3">
                      <Inbox className="w-12 h-12 text-muted-foreground/50" />
                      <div>
                        <p className="font-medium">{scopeFiltered ? 'No submissions match these filters' : 'No submissions yet'}</p>
                        <p className="text-sm text-muted-foreground">
                          {scopeFiltered ? 'Try a wider date range or clear the filters' : 'Form submissions will appear here'}
                        </p>
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                submissions.map((submission) => (
                  <TableRow
                    key={submission.id}
                    className={`cursor-pointer ${!submission.read ? 'bg-primary/5' : ''}`}
                    onClick={() => handleViewSubmission(submission)}
                    data-testid={`submission-row-${submission.id}`}
                  >
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleToggleStar(submission.id, submission.starred)}
                      >
                        {submission.starred ? (
                          <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                        ) : (
                          <Star className="w-4 h-4 text-muted-foreground" />
                        )}
                      </Button>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {!submission.read && (
                          <div className="w-2 h-2 rounded-full bg-primary" />
                        )}
                        <div>
                          <p className={`font-medium ${!submission.read ? 'text-foreground' : 'text-muted-foreground'}`}>
                            {submission.contact?.name || submission.data?.full_name || submission.data?.name || submission.contact?.email || 'Anonymous'}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {submission.data?.email || '-'}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <p className="text-sm text-muted-foreground truncate max-w-[180px]">
                        {pageTitles[submission.landing_page_id] || '-'}
                      </p>
                    </TableCell>
                    <TableCell>
                      <p className="text-sm text-muted-foreground truncate max-w-[200px]">
                        {submission.data?.message || submission.data?.enquiry_type || '-'}
                      </p>
                    </TableCell>
                    <TableCell>
                      {submission.source?.utm_campaign && (
                        <Badge variant="outline" className="text-xs">
                          {submission.source.utm_campaign}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                      {formatDate(submission.created_at)}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleToggleRead(submission.id, submission.read)}>
                            {submission.read ? (
                              <><Mail className="w-4 h-4 mr-2" />Mark Unread</>
                            ) : (
                              <><MailOpen className="w-4 h-4 mr-2" />Mark Read</>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleToggleStar(submission.id, submission.starred)}>
                            {submission.starred ? (
                              <><StarOff className="w-4 h-4 mr-2" />Unstar</>
                            ) : (
                              <><Star className="w-4 h-4 mr-2" />Star</>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => handleArchive(submission.id)}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Archive
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              {selectedSubmission?.contact?.name || selectedSubmission?.data?.full_name || selectedSubmission?.data?.name || 'Submission'}
            </DialogTitle>
            <DialogDescription>
              Submitted {formatDate(selectedSubmission?.created_at)}
              {selectedSubmission && pageTitles[selectedSubmission.landing_page_id]
                ? ` via ${pageTitles[selectedSubmission.landing_page_id]}`
                : ''}
            </DialogDescription>
          </DialogHeader>

          {selectedSubmission && (
            <ScrollArea className="max-h-[400px]">
              <div className="space-y-4">
                {/* Contact Info */}
                <div className="space-y-2">
                  {selectedSubmission.data?.email && (
                    <div className="flex items-center gap-3 p-2 rounded-lg bg-muted/50">
                      <Mail className="w-4 h-4 text-muted-foreground" />
                      <a href={`mailto:${selectedSubmission.data.email}`} className="text-sm hover:text-primary">
                        {selectedSubmission.data.email}
                      </a>
                    </div>
                  )}
                  {selectedSubmission.data?.phone && (
                    <div className="flex items-center gap-3 p-2 rounded-lg bg-muted/50">
                      <Phone className="w-4 h-4 text-muted-foreground" />
                      <a href={`tel:${selectedSubmission.data.phone}`} className="text-sm hover:text-primary">
                        {selectedSubmission.data.phone}
                      </a>
                    </div>
                  )}
                </div>

                <Separator />

                {/* Form Data */}
                <div className="space-y-3">
                  {Object.entries(selectedSubmission.data || {}).map(([key, value]) => {
                    if (['full_name', 'name', 'email', 'phone'].includes(key)) return null;
                    return (
                      <div key={key}>
                        <p className="text-xs text-muted-foreground uppercase tracking-wide">{key.replace(/_/g, ' ')}</p>
                        <p className="text-sm mt-1">{String(value) || '-'}</p>
                      </div>
                    );
                  })}
                </div>

                {/* Marketing consent */}
                {consentLabel(selectedSubmission.marketing_consent) && (
                  <>
                    <Separator />
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">Marketing consent</p>
                      <Badge
                        variant={selectedSubmission.marketing_consent ? 'default' : 'outline'}
                        className="mt-1"
                      >
                        {consentLabel(selectedSubmission.marketing_consent)}
                      </Badge>
                      {selectedSubmission.marketing_consent_text && (
                        <p className="text-xs text-muted-foreground mt-1">"{selectedSubmission.marketing_consent_text}"</p>
                      )}
                    </div>
                  </>
                )}

                {/* UTM source */}
                {Object.keys(UTM_LABELS).some((k) => selectedSubmission.source?.[k]) && (
                  <>
                    <Separator />
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">Campaign tags</p>
                      {Object.entries(UTM_LABELS).map(([key, label]) => (
                        selectedSubmission.source?.[key] ? (
                          <p key={key} className="text-sm">
                            <span className="text-muted-foreground">{label}:</span> {selectedSubmission.source[key]}
                          </p>
                        ) : null
                      ))}
                    </div>
                  </>
                )}

                {/* Metadata */}
                <Separator />
                <div className="text-xs text-muted-foreground space-y-1">
                  <p><Clock className="w-3 h-3 inline mr-1" />Submitted: {new Date(selectedSubmission.created_at).toLocaleString('en-ZA')}</p>
                  {selectedSubmission.ip_address && <p>IP: {selectedSubmission.ip_address}</p>}
                </div>
              </div>
            </ScrollArea>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => handleToggleStar(selectedSubmission?.id, selectedSubmission?.starred)}
            >
              {selectedSubmission?.starred ? (
                <><StarOff className="w-4 h-4 mr-2" />Unstar</>
              ) : (
                <><Star className="w-4 h-4 mr-2" />Star</>
              )}
            </Button>
            <Button
              variant="destructive"
              onClick={() => handleArchive(selectedSubmission?.id)}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Archive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
